import React, { useState } from "react";
import { IoChevronUp, IoChevronDown } from "react-icons/io5";
import type { OpeningHours } from "../../Manager/restaurant.types";
import SuggestEdit from "../Components/SuggestEdit";

interface OpeningHoursProps {
  openingHours: OpeningHours;
}

const parseTimeToMinutes = (timeStr: string): number => {
  if (!timeStr) return 0;
  const cleaned = timeStr.trim().toUpperCase();
  const isPM = cleaned.includes("PM");
  const [hourStr, minuteStr = "00"] = cleaned
    .replace(/(AM|PM)/g, "")
    .trim()
    .split(":");

  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (isPM && hour < 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;

  return hour * 60 + minute;
};


/** ✅ Returns a human-friendly open/close status */
export const getOpenStatus = (openingHours: OpeningHours, date = new Date()): string => {
  const daysOfWeek = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
  const todayKey = daysOfWeek[date.getDay()] as keyof OpeningHours;
  const hours = openingHours[todayKey];

  if (!hours) return "No hours available";

  if (hours.toLowerCase().includes("closed")) return "Closed today";

  const periods = hours.split(",").map(p => p.trim());
  const nowMinutes = date.getHours() * 60 + date.getMinutes();

  for (const period of periods) {
    const [openStr, closeStr] = period.split("-");
    if (!openStr || !closeStr) continue;

    const openMinutes = parseTimeToMinutes(openStr);
    const closeMinutes = parseTimeToMinutes(closeStr);

    if (nowMinutes >= openMinutes && nowMinutes <= closeMinutes) {
      const remainingMinutes = closeMinutes - nowMinutes;
      return remainingMinutes <= 60 ? `Open until ${closeStr.trim()}` : "Open now";
    }
  }

  const nextPeriod = periods.find(period => {
    const [openStr] = period.split("-");
    return nowMinutes < parseTimeToMinutes(openStr);
  });

  return nextPeriod ? `Closed, opens at ${nextPeriod.split("-")[0].trim()}` : "Closed now";
};

/** ✅ Component */
const OpenHours: React.FC<OpeningHoursProps> = ({ openingHours }) => {
  const [expanded, setExpanded] = useState(false);
  const todayKey = new Date()
    .toLocaleString("en-US", { weekday: "long" })
    .toLowerCase();
  const todayHours = openingHours[todayKey as keyof OpeningHours];
  const status = getOpenStatus(openingHours);
  const hasHours = Object.values(openingHours).some(day => day && day.trim() !== "");


  return (
    <div className="p-4 pb-6 lg:rounded-2xl border-b lg:border border-gray-300">
      {/* Header */}
      <div
        className="flex w-fullitems-center justify-between cursor-pointer lg:cursor-default"
        onClick={() => setExpanded((prev) => !prev)}
      >
          <div className="flex flex-col">
            <div className="flex justify-between">
              <h2 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-0">
                Opening Hours
              </h2>
              {status === "No hours available" && <SuggestEdit />}
            </div>

          {status && <span
            className={`${
              status.includes("Open") ? "text-green-600" : "text-red-500"
            }`}
          >
            {status}
          </span>}
        </div>

        {/* Icon for mobile */}
        <div className="lg:hidden text-gray-600">
          {expanded ? <IoChevronUp size={20} /> : <IoChevronDown size={20} />}
        </div>
      </div>

      {/* All days */}
      {hasHours && (
        <div
          className={`mt-4 overflow-hidden transition-all duration-300 ${
            expanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          } lg:max-h-full lg:opacity-100`}
        >
          <div className="space-y-3">
            {Object.entries(openingHours).map(([day, hours]) => {
              const displayHours = hours || "Closed"; // fallback for null
              const isToday = day === todayKey;

              return (
                <div key={day} className="flex justify-between items-center">
                  <span className="text-gray-700 capitalize font-medium">{day}</span>
                  <span className={`text-gray-900 ${isToday ? "font-semibold" : ""}`}>
                    {displayHours}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenHours;
